import React from 'react';
import { CollapsibleCards } from '../collapsible-cards';
import { CollapsibleContent } from '../collapsible-content';
import { Box as Wrapper, Flex, Text, Icon, Divider, Image } from '@tonic-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './security-concerns-section.module.css';

import CloudComplianceIcon from '../../../assets/landing/SVG/icon_1.svg';
import CloudNativeIcon from '../../../assets/landing/SVG/icon_2.svg';
import CloudDataCenterIcon from '../../../assets/landing/SVG/icon_3.svg';
// Landing Page pictures
import CloudGraphic from '../../../assets/landing/SVG/cloud_graphic.svg';
import ExternalLink from '../../external-link/external-link';

const DetailsBody = ({ title, paragraph, learnMoreLink }) => {
  return (
    <Wrapper>
      <Text size="xl" pb="2x" pt="4x" color="black">
        <Trans i18nKey="publicLandingPage.weRecommedToExplore" />
      </Text>
      {title && (
        <Flex pb="2x" pt="2x" direction="column" gap="2x">
          <Text size="4xl" mb="4x">
            {title}
          </Text>
          <Divider width="80px" height="3px" bg="red:70" />
        </Flex>
      )}
      <Text mt="5x" size="lg" color="gray:60" lineHeight="1.7">
        {paragraph}
      </Text>
      {learnMoreLink && (
        <Flex pt="6x" pb="5x" align="center">
          <ExternalLink href={learnMoreLink} showIcon={false} className={styles.learn_more}>
            <Text size="lg" fontWeight="semibold" color="black">
              <Trans i18nKey="publicLandingPage.learnMore" />
            </Text>
            <Icon
              icon="arrow-long-right"
              ml="2x"
              color="red:70"
              size="3x"
              className={styles.animated_arrow_right}
            />
          </ExternalLink>
        </Flex>
      )}
    </Wrapper>
  );
};

export const SecurityConcernsSection = () => {
  const { t } = useTranslation();
  return (
    <Wrapper
      width="900px"
      margin="0 auto"
      pt="16x"
      pb="100px"
      pl="8x"
      pr="8x"
      className={styles.security_concerns}
    >
      <Flex mb="80px" mt="80px" direction="row" justify="space-between">
        <Text className={styles.big_heading}>
          <Trans i18nKey="publicLandingPage.heading" />
        </Text>
        <Image src={CloudGraphic} width="250px" />
      </Flex>
      <CollapsibleCards
        cards={[
          {
            id: 'security-and-compliance',
            heading: t('publicLandingPage.demonstratingCloudVisibility'),
            icon: <Image src={CloudComplianceIcon} height="28px" />,
            className: styles.demonstrating_cloud_visibility,
            body: (
              <DetailsBody
                title={t('publicLandingPage.cloudOneConformity.title')}
                paragraph={t('publicLandingPage.cloudOneConformity.paragraph')}
                learnMoreLink={t('publicLandingPage.cloudOneConformity.link')}
              />
            ),
          },
          {
            id: 'cloud-native',
            heading: t('publicLandingPage.buildingCloudNative'),
            icon: <Image src={CloudNativeIcon} height="28px" />,
            className: styles.building_cloud_native,
            body: (
              <CollapsibleContent
                header={
                  <Text size="4xl" pb="2x" pt="4x" lineHeight="1.7">
                    <Trans i18nKey="publicLandingPage.securityAndVisibilityFirst" />
                  </Text>
                }
                data={[
                  {
                    id: 'cloud-conformity',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.applyingBestPracticeAndCompliance" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.cloudOneConformity.title')}
                        paragraph={t('publicLandingPage.cloudOneConformity.paragraph')}
                        learnMoreLink={t('publicLandingPage.cloudOneConformity.link')}
                      />
                    ),
                  },
                  {
                    id: 'file-storage-security',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.storageSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.storageSecurity.title')}
                        paragraph={t('publicLandingPage.storageSecurity.paragraph')}
                        learnMoreLink={t('publicLandingPage.storageSecurity.link')}
                      />
                    ),
                  },
                  {
                    id: 'open-source-security-snyk',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.snykSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.snyk.title')}
                        paragraph={t('publicLandingPage.snyk.paragraph')}
                        learnMoreLink={t('publicLandingPage.snyk.link')}
                      />
                    ),
                  },
                  {
                    id: 'application-security',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.applicationSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.applicationSecurity.title')}
                        paragraph={t('publicLandingPage.applicationSecurity.paragraph')}
                        learnMoreLink={t('publicLandingPage.applicationSecurity.link')}
                      />
                    ),
                  },
                  {
                    id: 'container-security',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.containerSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.containerSecurity.title')}
                        paragraph={t('publicLandingPage.containerSecurity.paragraph')}
                        learnMoreLink={t('publicLandingPage.containerSecurity.link')}
                      />
                    ),
                  },
                  {
                    id: 'network-security',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.networkSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.networkSecurity.title')}
                        paragraph={t('publicLandingPage.networkSecurity.paragraph')}
                        learnMoreLink={t('publicLandingPage.networkSecurity.link')}
                      />
                    ),
                  },
                ]}
              />
            ),
          },
          {
            id: 'datacenter-and-apps',
            heading: t('publicLandingPage.movingDataOrApplication'),
            icon: <Image src={CloudDataCenterIcon} height="28px" />,
            className: styles.moving_data_from_datacenter,
            body: (
              <CollapsibleContent
                header={
                  <Text size="4xl" pb="2x" pt="4x" lineHeight="1.7">
                    <Trans i18nKey="publicLandingPage.cloudOneConformityService" />
                  </Text>
                }
                data={[
                  {
                    id: 'cloud-conformity',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.applyingBestPracticeAndCompliance" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.cloudOneConformity.title')}
                        paragraph={t('publicLandingPage.cloudOneConformity.paragraph')}
                        learnMoreLink={t('publicLandingPage.cloudOneConformity.link')}
                      />
                    ),
                  },
                  {
                    id: 'file-storage-security',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.storageSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.storageSecurity.title')}
                        paragraph={t('publicLandingPage.storageSecurity.paragraph')}
                        learnMoreLink={t('publicLandingPage.storageSecurity.link')}
                      />
                    ),
                  },
                  {
                    id: 'open-source-security-snyk',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.snykSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.snyk.title')}
                        paragraph={t('publicLandingPage.snyk.paragraph')}
                        learnMoreLink={t('publicLandingPage.snyk.link')}
                      />
                    ),
                  },
                  {
                    id: 'application-security',
                    heading: (
                      <Text size="lg" color="black">
                        <Trans i18nKey="publicLandingPage.applicationSecurityService" />
                      </Text>
                    ),
                    body: (
                      <DetailsBody
                        title={t('publicLandingPage.applicationSecurity.title')}
                        paragraph={t('publicLandingPage.applicationSecurity.paragraph')}
                        learnMoreLink={t('publicLandingPage.applicationSecurity.link')}
                      />
                    ),
                  },
                ]}
              />
            ),
          },
        ]}
      />
    </Wrapper>
  );
};

SecurityConcernsSection.displayName = 'SecurityConcernsSection';

export default SecurityConcernsSection;
